import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import {
  Delete20Regular,
  Edit20Regular,
  MoreHorizontal20Regular,
} from '@fluentui/react-icons';
import { Handle, Position } from '@xyflow/react';
import { getIcon, getColor } from '../utils/nodeUtils';

/**
 * Traduit le type de nœud
 */
const getTranslatedNodeType = (nodeType, t) => {
  if (!nodeType) return t('ROUTING');
  
  const type = nodeType.toLowerCase();
  
  if (type.includes('customer choice') || type.includes('customerchoice') || type.includes('routing customer choise')) {
    return t('CUSTOMER_CHOICE');
  }
  if (type.includes('queue')) {
    return t('QUEUE');
  }
  if (type.includes('form')) {
    return t('FORM');
  }
  
  return nodeType;
};

/**
 * Composant pour afficher le label d'un nœud avec son menu contextuel
 */
export const NodeLabel = ({ 
  nodeId, 
  nodeType, 
  displayName,
  onEdit, 
  onDelete,
  showDelete = true,
  customerChoiceActions = null,
  additionalInfo = null
}) => {
  const { t } = useTranslation();
  return (
    <div style={{ padding: '12px', position: 'relative' }}>
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <button
            className="node-menu-btn"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: '-10px',
              right: '-8px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <MoreHorizontal20Regular />
          </button>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem icon={<Edit20Regular />} onClick={() => onEdit(nodeId)}>
              {t('EDIT')}
            </MenuItem>
            {showDelete && (
              <MenuItem icon={<Delete20Regular />} onClick={() => onDelete(nodeId)}>
                {t('DELETE')}
              </MenuItem>
            )}
          </MenuList>
        </MenuPopover>
      </Menu>
      
      {/* Handle d'entrée en haut pour recevoir les connexions */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: '#555',
          width: '10px',
          height: '10px',
        }}
      />
      
      {/* Si c'est un Customer Choice, créer plusieurs handles de sortie */}
      {customerChoiceActions && Object.keys(customerChoiceActions).map((_, index, arr) => {
        const handleCount = arr.length;
        const spacing = 100 / (handleCount + 1);
        return (
          <Handle
            key={`source-${index}`}
            id={`source-${index}`}
            type="source"
            position={Position.Bottom}
            style={{
              background: '#8b5cf6',
              left: `${spacing * (index + 1)}%`,
            }}
          />
        );
      })}
      
      {/* Si ce n'est PAS un Customer Choice, créer un seul handle de sortie en bas */}
      {!customerChoiceActions && (
        <Handle
          type="source"
          position={Position.Bottom}
          style={{
            background: '#555',
            width: '10px',
            height: '10px',
          }}
        />
      )}
      
      <div style={{ fontWeight: 600, fontSize: '13px', color: getColor(nodeType) }}>
        {getTranslatedNodeType(nodeType, t)}
      </div>
      <div style={{ fontSize: '11px', color: '#000', marginTop: '3px' }}>
        {displayName}
      </div>
      {additionalInfo && (
        <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
          {additionalInfo}
        </div>
      )}
    </div>
  );
};

/**
 * Label spécifique pour le nœud de début de conversation
 */
export const ChatFlowStartLabel = ({ onEdit }) => {
  const { t } = useTranslation();
  return (
    <div style={{ padding: '0', textAlign: 'center', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <button
            className="node-menu-btn"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: '-11px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <MoreHorizontal20Regular />
          </button>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem icon={<Edit20Regular />} onClick={() => onEdit('chatflow-start')}>
              {t('EDIT')}
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
      <div style={{ fontWeight: 600, fontSize: '14px', color: '#5b5fc7', whiteSpace: 'pre-line' }}>
        {t('CONVERSATION_START_LABEL')}
      </div>
      
      {/* Handle de sortie en bas */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: '#5b5fc7',
          width: '10px',
          height: '10px',
        }}
      />
    </div>
  );
};

/**
 * Label pour le nœud Greeting
 */
export const GreetingLabel = ({ enabledMessagesCount, onEdit, onDelete }) => {
  const { t } = useTranslation();
  return (
    <div style={{ padding: '12px', position: 'relative' }}>
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <button
            className="node-menu-btn"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: '-8px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <MoreHorizontal20Regular />
          </button>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem icon={<Edit20Regular />} onClick={() => onEdit('greeting')}>
              {t('EDIT')}
            </MenuItem>
            <MenuItem icon={<Delete20Regular />} onClick={() => onDelete('greeting')}>
              {t('DELETE')}
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
      {/* Handle d'entrée en haut */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: '#10b981',
          width: '10px',
          height: '10px',
        }}
      />
      
      <div style={{ fontWeight: 600, fontSize: '13px', color: '#10b981' }}>
        {t('GREETING_MESSAGE')}
      </div>
      <div style={{ fontSize: '11px', color: '#000', marginTop: '3px' }}>
        {enabledMessagesCount} {t('MESSAGES', { count: enabledMessagesCount })}
      </div>
      
      {/* Handle de sortie en bas */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: '#10b981',
          width: '10px',
          height: '10px',
        }}
      />
    </div>
  );
};
